import { HTTPError, TimeoutError } from 'ky';
import { ZodError } from 'zod';
import { AppError } from './AppError';

/** Map HTTP status → safe, PII-free user message. Never echo raw backend strings. */
const USER_MESSAGES: Record<number, string> = {
  400: 'That request could not be processed.',
  401: 'Please sign in to continue.',
  403: 'You do not have access to that.',
  404: 'We could not find what you were looking for.',
  409: 'That action conflicts with the current state. Refresh and try again.',
  422: 'Some of the information is invalid.',
  429: 'Too many requests — please slow down and try again.',
  500: 'Something went wrong on our side.',
  503: 'The service is temporarily unavailable.',
};

/** Convert any thrown value into the single AppError shape (ARCHITECTURE §3.6). */
export function normalizeError(error: unknown): AppError {
  if (error instanceof AppError) return error;

  if (error instanceof ZodError) {
    return new AppError({
      kind: 'validation',
      messageForUser: 'The server returned data we did not expect. Please try again.',
      cause: error,
    });
  }

  if (error instanceof TimeoutError) {
    return new AppError({
      kind: 'network',
      messageForUser: 'The request timed out. Check your connection and try again.',
      cause: error,
    });
  }

  if (error instanceof HTTPError) {
    const status = error.response.status;
    // ONLY 401 is 'auth' (the transport already hard-logs-out + redirects, so the global
    // handler stays silent for it). 403 is a permission rejection the user must SEE — classing
    // it 'auth' silently swallowed every "you can't do this" toast while leaving the user
    // signed in with no feedback at all.
    const kind = status === 401 ? 'auth' : 'http';
    return new AppError({
      kind,
      status,
      messageForUser: USER_MESSAGES[status] ?? 'The request failed. Please try again.',
      cause: error,
    });
  }

  // fetch() network failures surface as TypeError.
  if (error instanceof TypeError) {
    return new AppError({
      kind: 'network',
      messageForUser: 'Network error — please check your connection and try again.',
      cause: error,
    });
  }

  return new AppError({
    kind: 'unknown',
    messageForUser: 'An unexpected error occurred. Please try again.',
    cause: error,
  });
}
